import { db, storage } from '../firebase';
import {
  doc, getDoc, getDocs, setDoc, deleteDoc, writeBatch, deleteField,
  serverTimestamp, collection, query, where, documentId, getCountFromServer,
  orderBy, startAt, endAt, limit
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { FOUNDER_UID } from '../config';
import { visibilityOf, looksAutogeneratedName } from './conversions';

// Slugs we don't hand out as custom URLs: system/route words plus reserved
// personal + brand handles. Kept in sync with reservedUsername() in
// firestore.rules, which is the actual server-side guard -- this list only
// drives the friendly UI error.
const RESERVED_USERNAMES = new Set([
  'profile', 'login', 'signup', 'create', 'generate', 'my-recipes',
  'user', 'users', 'recipe', 'recipes', 'admin', 'settings', 'about',
  'privacy', 'terms', 'help', 'api', 'me', 'null', 'undefined',
  'nate', 'nateanderson', 'nathan', 'nathananderson', 'nate_anderson',
  'nathan_anderson', 'nathanlukeanderson', 'nathan_luke_anderson',
  'tastr', 'mogulseeker'
]);

// Validate/normalize a custom-URL slug. Pure -- unit-tested. Returns
// { ok: true, value } with the normalized slug, or { ok: false, error }.
// The founder's own uid may claim a reserved word for themself (mirrored
// server-side in firestore.rules' reservedUsername check) -- e.g. claiming
// "nate" for the account the reserved list is protecting in the first place.
export function validateUsername(raw, { uid } = {}) {
  const value = (raw || '').trim().toLowerCase().replace(/^@/, '');
  if (value.length === 0) return { ok: false, error: 'Enter a custom URL, or clear it to remove.' };
  if (value.length < 3) return { ok: false, error: 'Must be at least 3 characters.' };
  if (value.length > 30) return { ok: false, error: 'Must be 30 characters or fewer.' };
  if (!/^[a-z][a-z0-9_-]*$/.test(value)) {
    return { ok: false, error: 'Start with a letter; use only letters, numbers, hyphens, or underscores.' };
  }
  if (RESERVED_USERNAMES.has(value) && uid !== FOUNDER_UID) {
    return { ok: false, error: 'That URL is reserved. Try another.' };
  }
  return { ok: true, value };
}

// Returns the uid that owns a slug, or null if unclaimed.
export async function getUsernameOwner(username) {
  const snap = await getDoc(doc(db, 'usernames', username));
  return snap.exists() ? snap.data().uid : null;
}

// Resolve a /user/:id param that may be a raw uid OR a custom slug to a uid.
// Real uid links always work (we try the users doc directly first), then we
// fall back to a slug lookup. Returns null if neither resolves.
export async function resolveUserId(idOrUsername) {
  if (!idOrUsername) return null;
  try {
    const direct = await getDoc(doc(db, 'users', idOrUsername));
    if (direct.exists()) return idOrUsername;
  } catch (err) {
    // Anonymous requests get permission-denied here, not just an empty
    // snapshot -- Firestore can't evaluate resource.data.isPublic against a
    // document that doesn't exist (idOrUsername is a slug, not a real uid).
    // Fall through to the slug lookup either way.
  }
  return getUsernameOwner(idOrUsername);
}

// Claim `next` for uid, releasing `current` if different. Atomic batch: the
// create of usernames/{next} is denied by rules if the slug already exists,
// so the whole thing fails rather than stealing someone's URL. Caller must
// ensure next !== current (re-claiming your own slug would self-deny).
export async function setUsername(uid, next, current) {
  const batch = writeBatch(db);
  batch.set(doc(db, 'usernames', next), { uid });
  if (current && current !== next) {
    batch.delete(doc(db, 'usernames', current));
  }
  batch.set(doc(db, 'users', uid), { username: next, updatedAt: serverTimestamp() }, { merge: true });
  await batch.commit();
}

// Release the slug and clear it from the profile.
export async function clearUsername(uid, current) {
  const batch = writeBatch(db);
  if (current) batch.delete(doc(db, 'usernames', current));
  batch.set(doc(db, 'users', uid), { username: deleteField(), updatedAt: serverTimestamp() }, { merge: true });
  await batch.commit();
}

// ---- Follows -------------------------------------------------------------
// Doc ID "{followerUid}_{followedUid}" gives one follow per pair (see
// firestore.rules). Batch fetches reuse the chunked documentId() pattern.

const FOLLOWS_COLLECTION = 'follows';
const followDocId = (followerUid, followedUid) => `${followerUid}_${followedUid}`;

export async function followUser(followerUid, followedUid) {
  if (!followerUid || !followedUid || followerUid === followedUid) return;
  await setDoc(doc(db, FOLLOWS_COLLECTION, followDocId(followerUid, followedUid)), {
    followerUid,
    followedUid,
    createdAt: serverTimestamp()
  });
}

export async function unfollowUser(followerUid, followedUid) {
  await deleteDoc(doc(db, FOLLOWS_COLLECTION, followDocId(followerUid, followedUid)));
}

export async function isFollowing(followerUid, followedUid) {
  if (!followerUid || !followedUid) return false;
  const snap = await getDoc(doc(db, FOLLOWS_COLLECTION, followDocId(followerUid, followedUid)));
  return snap.exists();
}

// The uids a user follows.
export async function getFollowing(uid) {
  if (!uid) return [];
  const snap = await getDocs(query(
    collection(db, FOLLOWS_COLLECTION),
    where('followerUid', '==', uid)
  ));
  return snap.docs.map(d => d.data().followedUid);
}

// The uids following a user.
export async function getFollowers(uid) {
  if (!uid) return [];
  const snap = await getDocs(query(
    collection(db, FOLLOWS_COLLECTION),
    where('followedUid', '==', uid)
  ));
  return snap.docs.map(d => d.data().followerUid);
}

// Follower/following totals via cheap aggregation (no docs read).
export async function getFollowCounts(uid) {
  const [followers, following] = await Promise.all([
    getCountFromServer(query(collection(db, FOLLOWS_COLLECTION), where('followedUid', '==', uid))),
    getCountFromServer(query(collection(db, FOLLOWS_COLLECTION), where('followerUid', '==', uid)))
  ]);
  return { followers: followers.data().count, following: following.data().count };
}

// Batch-fetch user profiles by uid (one read per 10 via documentId() 'in').
// Chunk queries are all-or-nothing under security rules: for a signed-out
// caller (public profile's follower/following modal), one NON-public profile
// in a chunk denies the whole query. Fall back to per-doc reads so only the
// private profiles drop out, not everyone batched alongside them.
export async function getUsersByIds(ids) {
  const unique = [...new Set(ids)].filter(Boolean);
  if (unique.length === 0) return [];
  const chunks = [];
  for (let i = 0; i < unique.length; i += 10) {
    chunks.push(unique.slice(i, i + 10));
  }
  const chunkResults = await Promise.all(chunks.map(chunk =>
    getDocs(query(collection(db, 'users'), where(documentId(), 'in', chunk)))
      .then(s => s.docs.map(d => ({ uid: d.id, ...d.data() })))
      .catch(() => Promise.all(chunk.map(uid =>
        getDoc(doc(db, 'users', uid))
          .then(s => (s.exists() ? { uid: s.id, ...s.data() } : null))
          .catch(() => null)
      )).then(docs => docs.filter(Boolean)))
  ));
  return chunkResults.flat();
}

// Full profiles of everyone a user follows.
export async function getFollowingProfiles(uid) {
  return getUsersByIds(await getFollowing(uid));
}

// Full profiles of everyone following a user.
export async function getFollowerProfiles(uid) {
  return getUsersByIds(await getFollowers(uid));
}


// ---- Private preferences -------------------------------------------------
// Allergy prefs live in an owner-only subcollection rather than on the
// profile: the users read rule serves a profile to every signed-in user (and
// to the open web when public), and food allergies are health-adjacent data
// that has no business being broadly readable. Only the owner can touch this
// -- see the users/{uid}/private rule.

const PRIVATE_PREFS_DOC = 'preferences';
const privatePrefsRef = (uid) => doc(db, 'users', uid, 'private', PRIVATE_PREFS_DOC);

export async function getAllergyPreferences(uid) {
  if (!uid) return [];
  try {
    const snap = await getDoc(privatePrefsRef(uid));
    return snap.exists() ? (snap.data().allergies || []) : [];
  } catch (err) {
    // Never let a missing/denied prefs doc break a page render.
    console.error('Error reading allergy preferences:', err);
    return [];
  }
}

export async function setAllergyPreferences(uid, allergies) {
  await setDoc(privatePrefsRef(uid), {
    allergies: [...new Set(allergies)].sort(),
    updatedAt: serverTimestamp()
  }, { merge: true });
}

// ---- Chef directory ------------------------------------------------------
// Powers the "Chefs" category in Browse. Ranked by, in order:
//   1. mutual follows -- chefs followed by people the viewer already follows
//      (the strongest social signal, and what makes the list feel personal)
//   2. total followers
//   3. display name, alphabetical
//
// Ranking happens client-side over two queries: all users, and all follow
// edges. That is deliberate at the current size (tens of users, tens of
// follows) and avoids denormalizing a followerCount onto every profile plus
// the trigger and backfill that would need. SCALING NOTE: this stops being
// reasonable somewhere in the low thousands of follow edges -- at that point
// maintain users/{uid}.followerCount from a follows trigger and paginate,
// rather than fetching the whole edge list.
//
// Visibility: 'private' profiles are excluded. Everyone else is listed,
// which matches the model -- the default level is "visible to Tastr users",
// and this list is only shown to signed-in viewers.
const CHEF_POOL_LIMIT = 200;

export async function listChefs(viewerUid, { max = 60, includeSuspected = false } = {}) {
  const [usersSnap, followsSnap] = await Promise.all([
    // MUST constrain on searchable: the users read rule is a plain field
    // comparison so Firestore can contain list queries, which means an
    // unconstrained list is denied outright (and would also have returned
    // private profiles before that rule was tightened).
    getDocs(query(collection(db, 'users'), where('searchable', '==', true), limit(CHEF_POOL_LIMIT))),
    getDocs(collection(db, FOLLOWS_COLLECTION)).catch(() => null)
  ]);

  const edges = followsSnap ? followsSnap.docs.map(d => d.data()) : [];
  const followerCount = {};
  const viewerFollows = new Set();
  for (const e of edges) {
    if (!e.followedUid) continue;
    followerCount[e.followedUid] = (followerCount[e.followedUid] || 0) + 1;
    if (e.followerUid === viewerUid) viewerFollows.add(e.followedUid);
  }
  // "Followed by N people you follow."
  const mutual = {};
  for (const e of edges) {
    if (viewerFollows.has(e.followerUid)) {
      mutual[e.followedUid] = (mutual[e.followedUid] || 0) + 1;
    }
  }

  return usersSnap.docs
    .map(d => ({ uid: d.id, ...d.data() }))
    .filter(u => u.uid !== viewerUid && visibilityOf(u) !== 'private')
    .map(u => ({
      ...u,
      mutualCount: mutual[u.uid] || 0,
      followerCount: followerCount[u.uid] || 0,
      isFollowedByViewer: viewerFollows.has(u.uid),
      // Accounts still carrying the random token they were created with are
      // almost certainly bots or test signups. Hidden from the directory so
      // discovery isn't cluttered -- but surfaced to admins (flagged, not
      // filtered) so they can actually review and prune them.
      suspectedBot: looksAutogeneratedName(u.displayName) && !u.bio && !u.photoURL
    }))
    .filter(u => includeSuspected || !u.suspectedBot)
    .sort((a, b) =>
      b.mutualCount - a.mutualCount ||
      b.followerCount - a.followerCount ||
      (a.displayName || '').localeCompare(b.displayName || '')
    )
    .slice(0, max);
}

// ---- User search ---------------------------------------------------------
// Matching happens query-side against fields the onUserWritten trigger
// derives (searchTokens, displayNameLower), with the sweepUserSearchIndex
// scheduled job guaranteeing every profile is indexed -- including accounts
// that never sign in again. That guarantee is what lets this stay purely
// indexed: reads scale with the number of MATCHES, not the number of users,
// and no profile data is shipped to a searcher who didn't match it.
//
// Deliberately NOT the client-side pool scan BrowsePage uses for recipes:
// that ships every profile to whoever searches and silently stops finding
// people once the user base outgrows the pool -- the worst failure mode for
// search, because it looks identical to "no such person".
//
// Three lookups, unioned, each degrading independently so one failure can't
// blank the list:
//   1. exact @handle via usernames/{slug}  -- 1 read
//   2. whole-word token match              -- "bowen" finds "Mark Bowen"
//   3. displayName prefix                  -- as-you-type "mark b..."
// Private profiles carry searchable:false and are filtered out, except for
// the founder, whom the rules allow to read any profile.
const USER_SEARCH_LIMIT = 20;
// U+F8FF: a private-use codepoint sorting above ordinary text -- the standard
// Firestore prefix-range terminator.
const PREFIX_END = String.fromCharCode(0xf8ff);

export async function searchUsers(term, { includePrivate = false } = {}) {
  const q = (term || '').trim().toLowerCase();
  if (q.length < 2) return [];
  const users = collection(db, 'users');
  const indexedClause = includePrivate ? [] : [where('searchable', '==', true)];

  const [slugUid, tokenDocs, prefixDocs] = await Promise.all([
    getUsernameOwner(q).catch(() => null),
    getDocs(query(
      users, ...indexedClause,
      where('searchTokens', 'array-contains', q),
      limit(USER_SEARCH_LIMIT)
    )).then(s => s.docs).catch(() => []),
    getDocs(query(
      users, ...indexedClause,
      // PREFIX_END sorts above any ordinary character, making
      // [q, q+PREFIX_END] a prefix range.
      orderBy('displayNameLower'), startAt(q), endAt(q + PREFIX_END),
      limit(USER_SEARCH_LIMIT)
    )).then(s => s.docs).catch(() => [])
  ]);

  const byUid = new Map();
  const add = (uid, data) => {
    if (byUid.has(uid)) return;
    if (!includePrivate && visibilityOf(data) === 'private') return;
    byUid.set(uid, { uid, ...data });
  };

  // Exact handle first -- if you typed someone's username, that's who you meant.
  if (slugUid) {
    const exact = await getUserProfile(slugUid).catch(() => null);
    if (exact) add(exact.uid, exact);
  }
  for (const d of [...tokenDocs, ...prefixDocs]) add(d.id, d.data());
  return [...byUid.values()].slice(0, USER_SEARCH_LIMIT);
}

// Upload a profile avatar. Stored under avatars/{userId}/ so storage rules
// can scope writes to the owner (see storage.rules).
export async function uploadAvatarImage(file, userId) {
  const fileRef = ref(storage, `avatars/${userId}/${Date.now()}_${file.name}`);
  await uploadBytes(fileRef, file);
  return getDownloadURL(fileRef);
}

// Fetch a public user profile by uid. Returns null if the user doesn't exist.
export async function getUserProfile(uid) {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? { uid: snap.id, ...snap.data() } : null;
}

// Normalize a social handle/URL into a full profile URL. Accepts a bare
// handle ("@nate" / "nate") or a full URL and returns a usable https link.
export function socialUrl(platform, value) {
  if (!value) return null;
  const v = value.trim();
  if (/^https?:\/\//i.test(v)) return v;
  // Personal website: accept a bare domain and make it a usable https link.
  if (platform === 'website') return `https://${v.replace(/^\/+/, '')}`;
  const handle = v.replace(/^@/, '');
  if (platform === 'facebook') return `https://facebook.com/${handle}`;
  if (platform === 'twitter') return `https://x.com/${handle}`;
  if (platform === 'instagram') return `https://instagram.com/${handle}`;
  if (platform === 'linkedin') return `https://linkedin.com/in/${handle}`;
  return null;
}
